/**
 * Money formatting for the invoicing screens.
 *
 * ── Why this exists next to `FinanceApi.formatCurrency` ───────────────────────────────────────
 *
 * The finance API returns money in TWO representations and they are not interchangeable:
 *
 *   `totalIncCents: 27100`   integer minor units — the source of truth
 *   `total: "271.00"`        a JSON **string**, because Postgres `numeric` reaches TypeORM as one
 *
 * `formatCurrency` takes the value through `Intl.NumberFormat`, which means a float. That is fine
 * for the pre-existing payment/subscription screens and it is deliberately left alone, but a tax
 * invoice must not round-trip through a binary float on its way to the screen. So:
 *
 *   - `formatDollarString` formats the STRING field by string surgery — no `parseFloat` anywhere;
 *   - `formatCents` formats the integer field with integer arithmetic.
 *
 * Both produce `$1,234.56` (and `-$12.34` for a credit). Parsing happens in exactly one
 * direction — an operator's typed dollars into integer cents, in `dollarsToCents`.
 */

/** Group the integer part of a decimal string with commas. Pure string work. */
function groupThousands(digits: string): string {
	return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/**
 * Split a signed decimal STRING into its parts without arithmetic.
 * Returns null when the input is not a decimal string at all.
 */
function splitDecimalString(
	value: string,
): { negative: boolean; whole: string; fraction: string } | null {
	const trimmed = value.trim();
	const match = /^(-?)(\d*)(?:\.(\d*))?$/.exec(trimmed);
	if (!match || (match[2] === "" && (match[3] ?? "") === "")) {
		return null;
	}

	const negative = match[1] === "-";
	const whole = match[2] === "" ? "0" : match[2].replace(/^0+(?=\d)/, "");
	// The API always sends 2dp; pad a short fraction and truncate a long one rather than round —
	// rounding a *display* string would silently disagree with the cents field beside it.
	const fraction = ((match[3] ?? "") + "00").slice(0, 2);

	return { negative, whole, fraction };
}

/**
 * `"271.00"` -> `"$271.00"`, `"-12.5"` -> `"-$12.50"`, `"1234567.8"` -> `"$1,234,567.80"`.
 *
 * A value that is not a decimal string (null, undefined, `""`, anything else) renders as an em
 * dash — a missing amount must never render as `$0.00`, which reads as "nothing is owed".
 */
export function formatDollarString(value: string | null | undefined): string {
	if (value === null || value === undefined) {
		return "—";
	}

	const parts = splitDecimalString(String(value));
	if (!parts) {
		return "—";
	}

	return `${parts.negative ? "-" : ""}$${groupThousands(parts.whole)}.${parts.fraction}`;
}

/**
 * `27100` -> `"$271.00"`, `-1234` -> `"-$12.34"`.
 *
 * Integer arithmetic on the minor units, so it is exact for every value an invoice can hold.
 */
export function formatCents(cents: number | null | undefined): string {
	if (cents === null || cents === undefined || !Number.isFinite(Number(cents))) {
		return "—";
	}

	const asInt = Math.trunc(Number(cents));
	const negative = asInt < 0;
	const abs = Math.abs(asInt);
	const whole = String(Math.floor(abs / 100));
	const fraction = String(abs % 100).padStart(2, "0");

	return `${negative ? "-" : ""}$${groupThousands(whole)}.${fraction}`;
}

/** `27100` -> `271` — the number an antd `InputNumber` in dollars should show. */
export function centsToDollarsNumber(cents: number | null | undefined): number {
	if (cents === null || cents === undefined || !Number.isFinite(Number(cents))) {
		return 0;
	}
	return Math.trunc(Number(cents)) / 100;
}

/**
 * An operator's typed dollars -> integer cents. The ONLY parse direction in this module.
 *
 * `Math.round(dollars * 100)` alone is wrong at the boundaries a tax invoice actually hits:
 * `1.005 * 100` is `100.49999999999999` in IEEE-754, so a naive round gives 100 cents where the
 * decimal answer is 101. Re-reading the product at 15 significant digits (the round-trip
 * precision of a double) recovers the decimal the user typed, and the sign-aware round keeps
 * -1.005 and +1.005 symmetric.
 *
 * This is a PORT of `finance/src/common/money/cents.ts::dollarsToCents`; it must keep
 * agreeing with it.
 */
export function dollarsToCents(dollars: number | string | null | undefined): number {
	const asNumber = typeof dollars === "string" ? Number(dollars) : dollars;

	if (asNumber === null || asNumber === undefined || !Number.isFinite(asNumber)) {
		return 0;
	}

	const scaled = Number((asNumber * 100).toPrecision(15));
	return scaled < 0 ? -Math.round(-scaled) : Math.round(scaled);
}
